// Wire ↔ pantalla. El wire admite `null` y un `colorSlot` cualquiera; la pantalla no.
import type {
    CreateSegmentRequest,
    SegmentLogEntryResponse,
    SegmentResponse,
    SegmentUsageResponse,
    TestCustomerSegmentsResponse,
    UpdateSegmentRequest,
} from '@/api/services/loan-offerings/segments.contract';
import { sameIds, toColorSlot } from '../logic';
import type { DefinitionLogEntry } from '../components/DefinitionLogDialog';
import type { CustomerSegmentsTest, Segment, SegmentDraft, SegmentUsage } from '../types';

export function toSegment(wire: SegmentResponse): Segment {
    return {
        segmentId: wire.segmentId,
        name: wire.name,
        description: wire.description ?? '',
        colorSlot: toColorSlot(wire.colorSlot),
        cohortIds: [...wire.cohortIds],
        memberIds: [...wire.memberIds],
        isDefault: wire.isDefault,
        status: wire.status,
        version: wire.version,
        clientCount: wire.clientCount,
        pctTotal: wire.pctTotal,
        countedAt: wire.countedAt,
        countPending: wire.countPending,
        createdBy: wire.createdBy,
        updatedBy: wire.updatedBy,
        createdAt: wire.createdAt,
        updatedAt: wire.updatedAt,
    };
}

function trimmedOrNull(value: string): string | null {
    const trimmed = value.trim();
    return trimmed === '' ? null : trimmed;
}

export function toCreateRequest(draft: SegmentDraft): CreateSegmentRequest {
    return {
        name: draft.name.trim(),
        description: trimmedOrNull(draft.description),
        colorSlot: draft.colorSlot,
        cohortIds: [...draft.cohortIds],
        memberIds: [...draft.memberIds],
    };
}

/**
 * Parche parcial: solo los campos que cambiaron, más el motivo si se escribió. Un arreglo vacío SÍ
 * viaja — es «quítalos todos».
 */
export function toUpdateRequest(
    original: SegmentDraft,
    draft: SegmentDraft,
    reason = '',
): UpdateSegmentRequest {
    const patch: UpdateSegmentRequest = {};
    if (original.name !== draft.name) patch.name = draft.name.trim();
    if (original.description !== draft.description) {
        patch.description = trimmedOrNull(draft.description);
    }
    if (original.colorSlot !== draft.colorSlot) patch.colorSlot = draft.colorSlot;
    if (!sameIds(original.cohortIds, draft.cohortIds)) patch.cohortIds = [...draft.cohortIds];
    if (!sameIds(original.memberIds, draft.memberIds)) patch.memberIds = [...draft.memberIds];
    const trimmedReason = reason.trim();
    if (trimmedReason !== '') patch.reason = trimmedReason;
    return patch;
}

export function toUsage(wire: SegmentUsageResponse): SegmentUsage {
    return {
        policies: wire.policies.map((policy) => ({
            id: policy.policyId,
            name: policy.name,
            status: policy.status,
        })),
        promotions: wire.promotions.map((promotion) => ({
            id: promotion.promotionId,
            name: promotion.name,
            status: promotion.status,
        })),
    };
}

export function toCustomerSegmentsTest(wire: TestCustomerSegmentsResponse): CustomerSegmentsTest {
    return {
        retailCustomerId: wire.retailCustomerId,
        segments: wire.segments.map((trace) => ({
            segmentId: trace.segmentId,
            name: trace.name,
            matched: trace.matched,
            reason: trace.reason,
            matchedCohortIds: [...trace.matchedCohortIds],
        })),
        defaultApplies: wire.defaultApplies,
        evaluatorAvailable: wire.evaluatorAvailable,
    };
}

export function toSegmentLogEntry(wire: SegmentLogEntryResponse): DefinitionLogEntry {
    return {
        event: wire.event,
        occurredAt: wire.occurredAt,
        performedBy: wire.performedBy,
        reason: wire.reason,
        version: wire.version,
    };
}
